import { useEffect, useRef, useState } from "react";
import {
  Send, ThumbsUp, ThumbsDown, Sparkles, Rocket,
  MessageCircle, X, ArrowRight, Check,
} from "lucide-react";
import { ApiError, OwlApi } from "../lib/api";
import type { ChatHistoryMessage } from "../lib/api";
import { useSession } from "../lib/SessionContext";
import type { FundRatingChip, MarketChatResponse, OwlBlock } from "../lib/types";
import { FundChip, PeerChip } from "../components/FundChip";
import { OwlMascot } from "../components/OwlMascot";
import { BlockRenderer } from "./ChatBlocks";

/* 2026-05-27 (v9.2). anonymous chat memory.
 *
 * Signed-in users get authoritative server-side history (owl_queries),
 * but anonymous browsers had no memory at all. each chat call was
 * stateless. That broke the "follow-up" pattern (ask about silver,
 * then ask "and what about gold" → Owl had no idea what 'and' meant).
 *
 * Fix: maintain a tiny localStorage buffer of the current browser's
 * thread and pass it back to the chat endpoint on each call. Capped
 * at 6 exchanges (12 messages) so we never balloon request size; the
 * server re-applies the same cap defensively. Lives only as long as
 * the browser keeps localStorage (typical = forever, until cleared).
 */
const ANON_HISTORY_KEY = "owl.chat.localHistory.v1";
const ANON_HISTORY_MAX = 12;

function readAnonHistory(): ChatHistoryMessage[] {
  try {
    const raw = localStorage.getItem(ANON_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (m): m is ChatHistoryMessage =>
          m && (m.role === "user" || m.role === "assistant") &&
          typeof m.content === "string" && m.content.length > 0,
      )
      .slice(-ANON_HISTORY_MAX);
  } catch {
    return [];
  }
}

function writeAnonHistory(history: ChatHistoryMessage[]): void {
  try {
    localStorage.setItem(
      ANON_HISTORY_KEY,
      JSON.stringify(history.slice(-ANON_HISTORY_MAX)),
    );
  } catch {
    /* quota or private-mode. non-fatal */
  }
}

/* clearAnonHistory() intentionally omitted. once a user signs in
   their server-side history takes over, but we keep the localStorage
   buffer around in case they sign out and revert to anonymous (then
   their prior thread is still there). If we ever need a "clear chat"
   button, removeItem(ANON_HISTORY_KEY) is the one-liner. */

/**
 * Floating Ask-Owl button + slide-in drawer.
 *
 * 2026-07-02 (P4). UNGATED + STREAMING. The first answer flows without
 * an email — the old inline EmailGate (which blocked anonymous sends)
 * is gone; sign-in lives in the topbar and the post-3rd-answer waitlist
 * nudge stays. Answers stream in live over SSE (market-chat/stream)
 * with a sync-POST fallback if the stream can't be established.
 *
 * 2026-05-26. after 3 Owl answers in a session, inject a one-time
 * waitlist nudge card into the thread.
 *
 * Controlled from the parent so the magazine can prefill questions.
 */

type Msg =
  | { kind: "user"; text: string }
  | {
      kind: "owl";
      text: string;
      basedOn?: string[];
      aiAvailable?: boolean;
      queryId?: number;
      thumbs?: 1 | -1;
      funds?: FundRatingChip[];
      peers?: FundRatingChip[];
      categoryReferenced?: string | null;
      disclaimer?: string | null;
      // 2026-07-06 — server-owned generative-UI blocks. When renderMode is
      // "blocks", the bubble renders the animated components (+ caption) and
      // suppresses the prose text.
      blocks?: OwlBlock[];
      renderMode?: "blocks" | "prose";
      // 2026-07-02 (P4) – true while SSE deltas are still painting this
      // bubble. Feedback/chips arrive with the final `done` payload.
      streaming?: boolean;
    }
  | {
      kind: "redirect";
      message: string;
      redirect?: { label: string; href: string };
    }
  | { kind: "error"; text: string }
  // 2026-05-28 – `mode` distinguishes the two nudge variants:
  //   "join"         – anonymous or never-joined user, original
  //                    "Yes, add me" CTA.
  //   "confirm_auto" – signed-in user who was auto-enrolled via
  //                    /identify (silent default opt-in). Card asks
  //                    "Stay on the list?" with [Stay] [Opt out].
  //                    Transparent confirmation so opt-in isn't a
  //                    dark pattern.
  // `joined` stays for back-compat – true means the action was
  // taken (joined via CTA, or stayed in confirm_auto mode).
  | { kind: "waitlist_nudge"; joined: boolean; mode?: "join" | "confirm_auto" };

const SUGGESTIONS = [
  "What's happening in the markets right now?",
  "Top 5 midcap funds",
  "Why is gold rallying?",
  "Tell me about HDFC Flexi Cap",
];

// Show the nudge after this many Owl responses. Tunable in one place
// so we don't sprinkle magic numbers across the file.
const NUDGE_AFTER_RESPONSES = 3;

export function ChatDrawer({
  open,
  onOpenChange,
  prefill,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  prefill?: string | null;
}) {
  const {
    bumpQueryCount, status, email,
    waitlistJoined, markWaitlistJoined,
  } = useSession();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // One-time per session. once the nudge has been injected we don't
  // do it again, regardless of dismiss/join.
  const [nudgeShown, setNudgeShown] = useState(false);
  // 2026-05-27 (v9.2). only fetch server history once per mount.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && status === "signed_in") {
      const t = setTimeout(() => textareaRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open, status]);

  // 2026-05-27 (v9.2). when a SIGNED-IN user opens the drawer for the
  // first time in this mount, fetch their prior thread from the server
  // and pre-fill the messages array. Solves the mobile-feedback case
  // where the drawer opened empty and the user thought their old
  // conversation was lost. We only do this once per drawer-mount; if
  // they send a new message we don't re-fetch (would clobber the
  // in-flight conversation).
  useEffect(() => {
    if (!open || historyLoaded || status !== "signed_in") return;
    let cancelled = false;
    OwlApi.chatHistory()
      .then((res) => {
        if (cancelled) return;
        const seeded: Msg[] = (res.messages || []).map((m) =>
          m.role === "user"
            ? { kind: "user" as const, text: m.content }
            : { kind: "owl" as const, text: m.content },
        );
        // Only seed when there's actual history AND the drawer is
        // currently empty. never overwrite a live conversation.
        setMessages((curr) => (curr.length === 0 ? seeded : curr));
        setHistoryLoaded(true);
      })
      .catch(() => setHistoryLoaded(true));
    return () => { cancelled = true; };
  }, [open, status, historyLoaded]);

  // Carry a prefilled question from a parent click ("Ask Owl about
  // this fund"). Don't fire immediately – the user reviews + sends.
  useEffect(() => {
    if (open && prefill) setInput(prefill);
  }, [open, prefill]);

  // Track the LAST message's text length too so the view follows a
  // streamed answer as it grows, not just when a message is appended.
  const lastMsg = messages[messages.length - 1];
  const lastTextLen =
    lastMsg && "text" in lastMsg ? lastMsg.text.length : 0;
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, busy, lastTextLen]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && open) onOpenChange(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  /** Send a question. 2026-07-02 (P4): no email gate — the first answer
   * flows for everyone. Sign-in (topbar) still upgrades to server-side
   * history; the post-3rd-answer waitlist nudge is unchanged. */
  async function attemptSend(text: string) {
    const q = text.trim();
    if (!q || busy) return;
    await sendNow(q);
  }

  /** The actual send — streams the answer live, sync-POST fallback. */
  async function sendNow(q: string) {
    setMessages((m) => [...m, { kind: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      // 2026-05-27 (v9.2). anonymous users carry their local thread
      // server-side as `local_history` so follow-ups have context.
      // Signed-in users: pass undefined; the server reads owl_queries.
      const localHist = status === "anonymous" ? readAnonHistory() : undefined;
      // 2026-07-10 (founder: "text loading in animated style is bad"; "the cards
      // come and the text disappears"). We painted every SSE delta into a
      // provisional typewriter bubble — then DROPPED it and rendered blocks
      // instead, so the customer watched text type out and then vanish. It was
      // also genuinely slow: each delta re-parsed the WHOLE growing markdown
      // string, so a long answer cost O(n²) re-renders.
      //
      // Now: no typewriter. We still use the STREAM (its heartbeats stop nginx
      // cutting a slow answer) but ignore the deltas, showing one calm "thinking"
      // state until the authoritative `done` payload lands. The answer then
      // appears ONCE, in the standard structure documented on OwlBubble.
      const onText = undefined;
      let res;
      try {
        res = await OwlApi.chatStream(q, localHist, onText);
      } catch (streamErr) {
        // Fall back to the sync endpoint ONLY on transport-shaped
        // failures. A 4xx (esp. 429 rate limit) would just fail again —
        // and the retry would double the rate-limit burn and, on a
        // server hiccup, the LLM spend. (Review 2026-07-02.)
        if (streamErr instanceof ApiError &&
            streamErr.status >= 400 && streamErr.status < 500) {
          throw streamErr;
        }
        res = await OwlApi.chat(q, localHist);
      }
      // Drop the provisional streaming bubble; the final payload below is
      // authoritative (post-processed text + chips + query_id).
      setMessages((m) =>
        m.length && m[m.length - 1].kind === "owl" &&
        (m[m.length - 1] as Extract<Msg, { kind: "owl" }>).streaming
          ? m.slice(0, -1)
          : m,
      );
      // Persist the new exchange into local history for anonymous
      // users so the NEXT message has continuity.
      if (status === "anonymous" && res.in_scope && (res as MarketChatResponse).answer) {
        const updated: ChatHistoryMessage[] = [
          ...(localHist || []),
          { role: "user", content: q },
          { role: "assistant", content: (res as MarketChatResponse).answer! },
        ];
        writeAnonHistory(updated);
      }
      setMessages((m) => {
        const next: Msg[] = [...m];
        if (res.in_scope) {
          next.push({
            kind: "owl",
            text: (res as MarketChatResponse).answer ?? "Owl had nothing to say there.",
            basedOn: res.based_on,
            aiAvailable: res.ai_available,
            queryId: res.query_id,
            funds: res.funds_referenced,
            peers: res.peers_referenced,
            categoryReferenced: res.category_referenced,
            disclaimer: res.disclaimer,
            blocks: (res as MarketChatResponse).blocks,
            renderMode: (res as MarketChatResponse).render_mode,
          });
          bumpQueryCount();
        } else {
          next.push({
            kind: "redirect",
            message:
              res.message ??
              "Owl only handles market questions here. Personal portfolio questions belong in Owl's Notebook.",
            redirect: res.redirect,
          });
        }
        // 2026-05-28 – the nudge has two modes now:
        //   • mode="join"         – anonymous OR signed-in but not joined
        //                            (edge case). Original "Yes, add me" CTA.
        //   • mode="confirm_auto" – signed-in AND already joined (most
        //                            signed-in users after the /identify
        //                            silent default opt-in landed). Card
        //                            transparently says "we added you" and
        //                            offers an opt-out so the default
        //                            opt-in isn't a dark pattern.
        // The prior behaviour was "skip entirely if joined" – that hid
        // the auto-enrol from the user, which felt sneaky.
        const owlCount = next.filter((x) => x.kind === "owl").length;
        if (!nudgeShown && owlCount >= NUDGE_AFTER_RESPONSES) {
          const mode: "join" | "confirm_auto" =
            (waitlistJoined && status === "signed_in") ? "confirm_auto" : "join";
          next.push({ kind: "waitlist_nudge", joined: false, mode });
        }
        return next;
      });
      // Mark nudge shown OUTSIDE the setMessages callback so subsequent
      // sends don't double-inject (React batches state updates).
      setMessages((m) => {
        if (m.some((x) => x.kind === "waitlist_nudge")) {
          if (!nudgeShown) {
            setNudgeShown(true);
          }
        }
        return m;
      });
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 429
          ? `Owl's getting a lot of questions right now. Try again in ${e.retryAfter ?? 60} seconds.`
          : e instanceof Error
          ? e.message
          : "Something went wrong reaching Owl.";
      // Drop any half-painted streaming bubble BEFORE the error card —
      // a truncated answer must never sit in the thread looking finished.
      // (Review 2026-07-02.)
      setMessages((m) => {
        const cleaned =
          m.length && m[m.length - 1].kind === "owl" &&
          (m[m.length - 1] as Extract<Msg, { kind: "owl" }>).streaming
            ? m.slice(0, -1)
            : m;
        return [...cleaned, { kind: "error", text: msg }];
      });
    } finally {
      setBusy(false);
    }
  }

  async function rate(idx: number, thumbs: 1 | -1) {
    const msg = messages[idx];
    if (!msg || msg.kind !== "owl" || !msg.queryId) return;
    setMessages((arr) =>
      arr.map((m, i) =>
        i === idx && m.kind === "owl" ? { ...m, thumbs } : m,
      ),
    );
    try {
      await OwlApi.feedback(msg.queryId, thumbs);
    } catch {
      setMessages((arr) =>
        arr.map((m, i) =>
          i === idx && m.kind === "owl" ? { ...m, thumbs: undefined } : m,
        ),
      );
    }
  }

  async function joinWaitlistFromNudge(idx: number) {
    try {
      await OwlApi.waitlist({ source: "chat_nudge" });
      // 2026-05-27 (v9.2). flip the in-context waitlist flag so future
      // drawer-opens (or future sessions before /me re-fetches) skip
      // the nudge. Without this, a user who joined via the in-chat
      // nudge could still see the nudge on the next page load.
      markWaitlistJoined();
      setMessages((arr) =>
        arr.map((m, i) =>
          i === idx && m.kind === "waitlist_nudge" ? { ...m, joined: true } : m,
        ),
      );
    } catch {
      // Silent on failure – the user can use the page Waitlist section.
    }
  }

  /**
   * 2026-05-26. user dismissed the post-chat waitlist confirmation.
   * Replace the nudge card with a quiet ack so it disappears, and
   * the `nudgeShown` flag (set on insertion) already prevents a
   * re-injection later in the session.
   */
  function dismissNudge(idx: number) {
    setMessages((arr) =>
      arr.filter((_, i) => i !== idx),
    );
  }

  // 2026-05-28 — `optOutFromAutoNudge` removed. The confirm_auto card
  // no longer renders an in-chat opt-out button (per product call:
  // the chat surface shouldn't be a churn funnel). Opt-out lives in
  // the email footer / future /account settings. The `/waitlist/opt-
  // out` API endpoint still exists for those surfaces.

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          aria-label="Open Ask Owl chat"
          className="fixed bottom-20 right-6 z-40 flex items-center gap-2 rounded-full bg-sky-500 px-5 py-3 text-sm font-bold text-white shadow-xl shadow-sky-900/30 transition hover:scale-[1.03] hover:bg-sky-600 sm:bottom-24"
        >
          <MessageCircle className="h-4 w-4" aria-hidden />
          Ask Owl
        </button>
      )}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-stretch sm:justify-end"
          role="dialog"
          aria-modal="true"
          aria-label="Ask Owl"
        >
          <button
            type="button"
            aria-label="Close chat"
            onClick={() => onOpenChange(false)}
            className="absolute inset-0 bg-nyvo-950/55 backdrop-blur-xs"
          />
          <div className="relative z-10 flex h-[88vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:h-screen sm:max-w-md sm:rounded-l-3xl sm:rounded-t-none">
            <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div className="flex items-center gap-2">
                <span aria-hidden className="text-lg">🦉</span>
                <div>
                  <div className="text-sm font-bold text-nyvo-900">Ask Owl</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">
                    {status === "signed_in" && email
                      ? `Saved to ${email}`
                      : "Ask away – no sign-up needed"}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label="Close"
                className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {messages.length === 0 ? (
                <EmptyState onPick={attemptSend} />
              ) : (
                <div className="space-y-3">
                  {messages.map((m, i) => (
                    <Bubble
                      key={i}
                      msg={m}
                      onRate={(t) => rate(i, t)}
                      onJoin={() => joinWaitlistFromNudge(i)}
                      onDismiss={() => dismissNudge(i)}
                    />
                  ))}
                  {/* 2026-07-10: the typewriter is gone, so this is the ONLY
                      in-flight affordance. It used to be suppressed whenever the
                      last bubble was an owl one — i.e. exactly when the streaming
                      bubble existed. Now it simply shows while a request is open. */}
                  {busy && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="pulse-dot" aria-hidden />
                      Owl is thinking...
                    </div>
                  )}
                  <div ref={listEndRef} />
                </div>
              )}
            </div>

            <Composer
              value={input}
              onChange={setInput}
              onSubmit={() => attemptSend(input)}
              busy={busy}
              textareaRef={textareaRef}
            />
            {/* Standing RIA context: clear house views, personal suitability when needed. */}
            <p className="px-5 pb-3 pt-1.5 text-center text-[10px] leading-snug text-slate-400">
              {"Owl shares NYVO's market and fund views. Personal suitability depends on your goals, horizon and portfolio."}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

/* ──────────── parts ──────────── */

function EmptyState({ onPick }: { onPick: (q: string) => void }) {
  return (
    <div className="space-y-3">
      {/* 2026-05-27. mascot greeting in the empty state so the drawer
          opens warm instead of clinical. Centered, modest size. chat
          drawer is narrow, a hero-size owl would dominate. */}
      <div className="flex flex-col items-center gap-2 pt-1 pb-2 text-center">
        <OwlMascot size="chat" />
        <p className="text-sm text-slate-600">
          Ask Owl anything about markets or fund ratings. Plain English
          answers, two to three sentences.
        </p>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
          <Sparkles className="h-3 w-3" aria-hidden />
          Try one of these
        </div>
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-nyvo-900 transition hover:border-sky-300 hover:bg-sky-50"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Tiny Markdown renderer for chat answers.
 *
 * Why hand-rolled instead of a library: the chat answer surface is
 * narrow (5 message blocks, no tables/images/code), and pulling in
 * react-markdown + remark-gfm would add ~25KB gzipped for what amounts
 * to bullets and bold text. So we do the smallest thing that works:
 *
 *  - Lines starting with "- " or "* " become real <li> items inside a
 *    single <ul>. Consecutive bullet lines belong to the same list;
 *    a blank line or a non-bullet line closes the list.
 *  - **bold** spans become <strong>.
 *  - Everything else renders as a <p> (paragraph per line group).
 *
 * No HTML escaping work is needed. React's JSX interpolation already
 * escapes the text we drop in via `{...}`. Bullet/bold parsing operates
 * on the source string before JSX, so injection isn't a vector.
 */
function ChatMarkdown({ text }: { text: string }) {
  if (!text || !text.trim()) return null;
  // Normalise line endings; we work line-by-line below.
  const lines = text.replace(/\r\n/g, "\n").split("\n");

  // Group consecutive bullet lines into ULs; other lines into Ps.
  type Block =
    | { kind: "ul"; items: string[] }
    | { kind: "p"; text: string };
  const blocks: Block[] = [];
  for (const raw of lines) {
    const line = raw.trimEnd();
    const bulletMatch = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bulletMatch) {
      const last = blocks[blocks.length - 1];
      if (last && last.kind === "ul") last.items.push(bulletMatch[1]);
      else blocks.push({ kind: "ul", items: [bulletMatch[1]] });
      continue;
    }
    if (!line.trim()) {
      // Blank line closes any open list; otherwise no-op.
      continue;
    }
    blocks.push({ kind: "p", text: line.trim() });
  }

  // Render a single string with **bold** spans broken into nodes.
  function renderInline(s: string): React.ReactNode[] {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      const m = /^\*\*([^*]+)\*\*$/.exec(p);
      if (m) return <strong key={i} className="font-bold text-nyvo-900">{m[1]}</strong>;
      return <span key={i}>{p}</span>;
    });
  }

  return (
    <>
      {blocks.map((b, i) =>
        b.kind === "ul" ? (
          <ul key={i} className="my-1 list-none space-y-1.5 pl-0">
            {b.items.map((item, j) => (
              <li key={j} className="flex gap-1.5 text-sm leading-relaxed">
                <span aria-hidden className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
                <span>{renderInline(item)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p
            key={i}
            className={
              i === 0 ? "text-sm leading-relaxed"
                      : "mt-1.5 text-sm leading-relaxed"
            }
          >
            {renderInline(b.text)}
          </p>
        ),
      )}
    </>
  );
}

function Bubble({
  msg, onRate, onJoin, onDismiss,
}: {
  msg: Msg;
  onRate: (t: 1 | -1) => void;
  onJoin: () => void;
  onDismiss: () => void;
}) {
  if (msg.kind === "user") {
    return (
      <div className="ml-auto max-w-[88%] rounded-2xl rounded-tr-sm bg-sky-500 px-3 py-2 text-sm text-white">
        {msg.text}
      </div>
    );
  }
  if (msg.kind === "owl") {
    const hasFunds = msg.funds && msg.funds.length > 0;
    // 2026-05-26 (chat polish v2). suppress the "Other Healthy in {cat}"
    // peer cards in two cases:
    //   • When the chat answer already lists 5+ funds (top-N path).
    //     Re-rendering 3 more cards underneath repeats funds the user
    //     just read in bullet form.
    //   • When the backend explicitly returned no peers (personal-fund
    //     mode does this. alternatives would feel like a sales nudge).
    const fundsCount = msg.funds?.length ?? 0;
    const hasPeers = (msg.peers?.length ?? 0) > 0 && fundsCount < 5;

    // ── THE STANDARD ANSWER STRUCTURE (founder call 2026-07-10) ──────────────
    // Every Owl answer renders in exactly one of two shapes, decided by the
    // SERVER (render_mode), never by the client, and never mid-flight:
    //
    //   A. VISUAL  (fund intents: top-N, compare, one fund)
    //        one-line caption  →  animated blocks  →  disclaimer  →  feedback
    //        No prose paragraph. No duplicate chip stack (the blocks ARE the cards).
    //
    //   B. PROSE   (concept / market questions — where Owl should actually talk)
    //        answer text  →  fund chips (if any)  →  peers  →  disclaimer  →  feedback
    //
    // There is no third, in-between state. The old typewriter produced one: text
    // streamed in, then vanished as the blocks replaced it. Text now appears once,
    // already final. (`streaming` is retained on the type only for the seeded
    // server-history path; no bubble is ever created in that state now.)
    const useBlocks = msg.renderMode === "blocks" && !!msg.blocks?.length;
    if (useBlocks) {
      return (
        <div className="max-w-[95%]">
          <BlockRenderer blocks={msg.blocks!} />
          {msg.disclaimer && (
            <p className="mt-1.5 px-1 text-[10px] italic leading-snug text-slate-500">{msg.disclaimer}</p>
          )}
          {msg.queryId && (
            <div className="mt-1 flex items-center justify-end gap-1 pl-1 text-[10px] text-slate-500">
              <FeedbackBtn active={msg.thumbs === 1} onClick={() => onRate(1)} Icon={ThumbsUp} label="Helpful" />
              <FeedbackBtn active={msg.thumbs === -1} onClick={() => onRate(-1)} Icon={ThumbsDown} label="Not helpful" />
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="max-w-[92%]">
        <div className="rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-nyvo-900">
          <ChatMarkdown text={msg.text} />
        </div>
        {hasFunds && (
          <div className="mt-2 space-y-1.5">
            {msg.funds!.map((f) => <FundChip key={f.scheme_code} chip={f} />)}
          </div>
        )}
        {hasPeers && (
          <div className="mt-2">
            <div className="mb-1 px-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Other Healthy in {msg.categoryReferenced || "this category"}
            </div>
            <div className="space-y-1">
              {msg.peers!.map((p) => <PeerChip key={p.scheme_code} chip={p} />)}
            </div>
          </div>
        )}
        {hasFunds && msg.disclaimer && (
          <p className="mt-1.5 px-1 text-[10px] italic leading-snug text-slate-500">
            {msg.disclaimer}
          </p>
        )}
        {/* 2026-05-27. "Based on: macro:silver, sector:NIFTYIT, …"
            chips removed. They were dev-friendly debug strings that
            looked raw to a customer (`macro:silver` reads as code).
            The thumbs feedback control stays right-aligned. */}
        {msg.queryId && (
          <div className="mt-1 flex items-center justify-end gap-1 pl-1 text-[10px] text-slate-500">
            <FeedbackBtn active={msg.thumbs === 1} onClick={() => onRate(1)} Icon={ThumbsUp} label="Helpful" />
            <FeedbackBtn active={msg.thumbs === -1} onClick={() => onRate(-1)} Icon={ThumbsDown} label="Not helpful" />
          </div>
        )}
      </div>
    );
  }
  if (msg.kind === "redirect") {
    // 2026-05-26 (chat polish v2). never link to Jarvis from public
    // Owl. The backend now always returns either a `#waitlist`-prefixed
    // in-page anchor (preferred for portfolio refusals. pitches the
    // NYVO Money waitlist) or nothing. Treat the absence of a "#"
    // prefix as a bug; we fall through to a button that scrolls to
    // the waitlist section regardless rather than open Jarvis.
    const href = msg.redirect?.href ?? "";
    const isAnchor = href.startsWith("#");
    return (
      <div className="max-w-[92%] rounded-2xl rounded-tl-sm border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-nyvo-900">
        <p>{msg.message}</p>
        {msg.redirect && (
          <button
            type="button"
            onClick={() => {
              // In-page scroll. Strip any leading "#" so getElementById
              // gets the bare id. If the section is missing, fall back to
              // the top-of-page so the user still sees the waitlist hero.
              const target = (isAnchor ? href.slice(1) : "waitlist") || "waitlist";
              const el = document.getElementById(target);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-sky-700 hover:underline"
          >
            {msg.redirect.label}
            <ArrowRight className="h-3 w-3" aria-hidden />
          </button>
        )}
      </div>
    );
  }
  if (msg.kind === "waitlist_nudge") {
    return (
      <WaitlistNudgeCard
        joined={msg.joined}
        mode={msg.mode ?? "join"}
        onJoin={onJoin}
        onDismiss={onDismiss}
      />
    );
  }
  return (
    <div className="max-w-[92%] rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
      {msg.text}
    </div>
  );
}

/**
 * 2026-05-26. this is now THE waitlist + newsletter consent moment.
 * Identify (email collection) no longer asks for consent; we wait
 * until the user has actually seen 3 Owl answers, then ask once.
 *
 * Honest consent timing: a "yes" here means they're saying yes
 * because they liked what they saw, not because they were nudged
 * into a pre-checked box during sign-up.
 *
 * If the user dismisses (Not now), we silently never ask again in
 * this session. bottom-of-page Waitlist section is the only other
 * surface, and it's an explicit form they have to fill out.
 */
function WaitlistNudgeCard({
  joined, mode, onJoin, onDismiss,
}: {
  joined: boolean;
  mode: "join" | "confirm_auto";
  onJoin: () => void;
  onDismiss: () => void;
}) {
  // Already-joined success state – only reached when user clicked
  // "Yes, add me" in the original join CTA. The confirm_auto mode
  // never sets joined=true; opt-out flow replaces the card outright.
  if (joined) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-sky-300 bg-sky-50 px-3 py-2.5 text-sm text-sky-900 animate-fade-in">
        <Check className="h-4 w-4 text-sky-600" aria-hidden />
        <span>You're on the list. We'll email when the app opens.</span>
      </div>
    );
  }
  // 2026-05-28 – transparent confirmation for users who were auto-
  // enrolled via /identify (silent default opt-in).
  //
  // 2026-05-28 (revision per user feedback) – dropped the in-card
  // opt-out button. Once a user is on the list, the chat surface
  // shouldn't be a churn funnel. Opt-out lives elsewhere (email
  // footer, future /account settings).
  if (mode === "confirm_auto") {
    return (
      <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sky-900 animate-fade-in">
        <div className="flex items-start gap-2.5">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" aria-hidden />
          <div className="flex-1">
            <p className="text-sm font-bold">You're on the NYVO Money waitlist.</p>
            <p className="mt-1 text-xs leading-relaxed text-sky-900/85">
              We'll email you when the full NYVO Money app opens – built
              for <em>your</em> portfolio, not just Owl's market view.
            </p>
            <div className="mt-3">
              <button
                type="button"
                onClick={onDismiss}
                className="inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-sky-700"
              >
                Got it
                <Check className="h-3 w-3" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Original "Yes, add me" CTA (anonymous or signed-in-but-not-joined).
  return (
    <div className="sky-panel rounded-2xl p-4 text-white animate-fade-in">
      <div className="flex items-start gap-2.5">
        <Rocket className="mt-0.5 h-4 w-4 shrink-0 text-white" aria-hidden />
        <div className="flex-1">
          <p className="text-sm font-bold">Now that you've seen Owl in action…</p>
          <p className="mt-1 text-xs leading-relaxed text-white/90">
            Want us to add your email to the{" "}
            <strong className="font-bold">NYVO Money waitlist</strong>{" "}
            and{" "}
            <strong className="font-bold">weekly newsletter</strong>?
            The full app does this for <em>your</em> portfolio.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onJoin}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-bold text-sky-700 transition hover:bg-sky-50"
            >
              Yes, add me
              <ArrowRight className="h-3 w-3" aria-hidden />
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/25"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedbackBtn({
  active, onClick, Icon, label,
}: { active: boolean; onClick: () => void; Icon: React.ElementType; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`rounded p-1 transition ${
        active ? "bg-sky-100 text-sky-700"
               : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
      }`}
    >
      <Icon className="h-3 w-3" aria-hidden />
    </button>
  );
}

function Composer({
  value, onChange, onSubmit, busy, textareaRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  busy: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}) {
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      className="border-t border-slate-200 bg-slate-50 p-3"
    >
      <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-white p-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(); }
          }}
          rows={1}
          placeholder="Ask Owl about markets or fund ratings..."
          className="max-h-28 flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-nyvo-900 placeholder:text-slate-400 focus:outline-hidden"
        />
        <button
          type="submit"
          disabled={busy || value.trim().length === 0}
          aria-label="Send"
          className="rounded-full bg-sky-500 p-2 text-white transition hover:bg-sky-600 disabled:opacity-40"
        >
          <Send className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <p className="mt-1.5 px-1 text-[10px] text-slate-400">
        Enter to send · Owl observes and explains, Owl never advises.
      </p>
    </form>
  );
}
